import { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { type MaasModel } from '@/proto/agentplatform/v2/maas_pb';
import { groupModels, formatContextLength, type ModelGroup } from '@/utils/model-display';

interface Props {
  /** 平铺模型列表，组件内部按 modelType 分组 */
  models?: MaasModel[];
  /** 已分组的模型列表（需要自定义分组时使用，如 Failover 的「探测模型」）；优先于 models */
  groups?: ModelGroup[];
  /** 受控值；也可由 antd Form.Item 注入 */
  value?: string;
  /** 变更回调；也可由 antd Form.Item 注入 */
  onChange?: (value: string) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  /** 触发器样式，默认 h-8（与 antd 输入框对齐）；Agent 模版表单传 h-10 */
  triggerClassName?: string;
}

/**
 * MaaS 模型下拉：面板内置搜索框、按模型类型分组、展示上下文长度。
 * 既可受控使用，也可直接作为 antd Form.Item 子组件使用。
 */
export default function MaasModelSelect({
  models,
  groups,
  value,
  onChange,
  id,
  placeholder = '请选择模型',
  disabled,
  triggerClassName = 'h-8',
}: Props) {
  const groupedModels = useMemo(() => groups ?? groupModels(models ?? []), [groups, models]);

  return (
    <Select value={value ?? ''} onValueChange={(val) => onChange?.(val)}>
      <SelectTrigger id={id} className={triggerClassName} disabled={disabled}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent searchable searchPlaceholder="搜索模型...">
        {groupedModels.map((group) => (
          <SelectGroup key={group.label}>
            <SelectLabel>{group.label}</SelectLabel>
            {group.models.map((m) => {
              const ctxLabel = formatContextLength(m.contextLength);
              const searchStr = `${m.name} ${m.modelType} ${ctxLabel}`;
              return (
                <SelectItem key={m.name} value={m.name} searchKeywords={searchStr}>
                  <span className="flex items-center justify-between w-full gap-2">
                    <span className="truncate">{m.name}</span>
                    {ctxLabel && (
                      <span className="text-xs text-muted-foreground shrink-0">{ctxLabel} ctx</span>
                    )}
                  </span>
                </SelectItem>
              );
            })}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}
